import { IMcpUser, IMcpUserUpsert } from './types';

export const NOTE_MAX = 500;

/** El interruptor de costos en el formulario: según el rol, sí o no. */
export type CommercialChoice = 'role' | 'yes' | 'no';

export const commercialToChoice = (value: boolean | null): CommercialChoice =>
	value === true ? 'yes' : value === false ? 'no' : 'role';

export const choiceToCommercial = (choice: CommercialChoice): boolean | null =>
	choice === 'yes' ? true : choice === 'no' ? false : null;

export const COMMERCIAL_LABELS: Record<CommercialChoice, string> = {
	role: 'Según su rol del Power',
	yes: 'Sí',
	no: 'No',
};

/** Para la tabla: qué ve hoy y por qué. */
export const describeCommercial = (value: boolean | null): string =>
	value === true ? 'Sí' : value === false ? 'No' : 'Según rol';

/** El formulario de un usuario. */
export interface McpUserForm {
	username: string;
	areas: string[];
	enabled: boolean;
	commercial: CommercialChoice;
	note: string;
}

export const emptyForm = (): McpUserForm => ({
	username: '',
	areas: [],
	enabled: true,
	commercial: 'role',
	note: '',
});

export const userToForm = (user: IMcpUser): McpUserForm => ({
	username: user.username,
	areas: [...user.areas],
	enabled: user.enabled,
	commercial: commercialToChoice(user.commercialAccess),
	note: user.note ?? '',
});

/** Lo que se manda: las áreas en el orden de la API, la nota sin espacios de más (vacía = sin nota). */
export const formToBody = (form: McpUserForm, areaOrder: string[]): IMcpUserUpsert => {
	const note = form.note.trim();
	return {
		username: form.username.trim(),
		areas: areaOrder.filter((area) => form.areas.includes(area)),
		enabled: form.enabled,
		commercialAccess: choiceToCommercial(form.commercial),
		note: note === '' ? null : note,
	};
};

/** ¿Cambió algo respecto de lo guardado? Sin cambios no se llama a la API. */
export const sameAsSaved = (body: IMcpUserUpsert, user: IMcpUser): boolean =>
	body.enabled === user.enabled &&
	body.commercialAccess === user.commercialAccess &&
	(body.note ?? null) === (user.note ?? null) &&
	body.areas.length === user.areas.length &&
	body.areas.every((area) => user.areas.includes(area));

/** Los mismos límites que la API. `null` si está bien. */
export function validateForm(form: McpUserForm): string | null {
	if (form.username.trim() === '') return 'Elegí un usuario del Power';
	if (form.username.trim().length > 120) return 'El usuario tiene hasta 120 caracteres';
	if (form.note.trim().length > NOTE_MAX) return `La nota tiene hasta ${NOTE_MAX} caracteres`;
	return null;
}

/** El mensaje para el usuario según lo que respondió la API. */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const describeMcpError = (error: any, action: 'load' | 'save'): string => {
	const status: number | undefined = error?.response?.status;
	const apiMessage: string | undefined = error?.response?.data?.message;
	if (status === 403) return apiMessage || 'Solo un Super Admin puede administrar el conector.';
	if (status === 409) return apiMessage || 'Alguien cambió la lista mientras tanto: recargá la página.';
	if (status === 404 || status === 405)
		return 'La Support API todavía no tiene esta función: falta deployar la versión nueva o prender el conector.';
	if (status === 400 && apiMessage) return `La API rechazó los datos: ${apiMessage}`;
	if (status === 429) return 'Demasiados pedidos seguidos: probá de nuevo en un minuto.';
	return action === 'load' ? 'No se pudo cargar la lista del conector.' : apiMessage || 'No se pudo guardar.';
};

/** "30/10/2026 14:05", o "—". */
export const formatDateTime = (iso: string | null): string => {
	if (!iso) return '—';
	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) return '—';
	return date.toLocaleString('es-UY', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});
};
